import Link from 'next/link';

/**
 * V2.11 J4 (2026-05-20) — single source of truth for rendering the
 * CEA logo on the page. Two variants:
 *   - horizontal: top-bar / header use. Native aspect ≈ 3.3:1 (zh) /
 *                 3.9:1 (en); we target a fixed height in px and let
 *                 the width auto.
 *   - vertical:   login page hero, applied-tile use. Native aspect
 *                 ≈ 0.77:1 — stack: 标识 on top, 中文字 below.
 *
 * Assets live in apps/web/public/brand/ as transparent-bg PNGs (built
 * from logo/*.svg via scripts/extract-logos.py — the SVGs Bytedance /
 * Figma exports are 2048px squares with cream backgrounds, useless for
 * page use until we run the background-strip step).
 *
 * Locale switches the wordmark between zh + en. We don't try to detect
 * locale client-side; the consumer passes it in (server can read from
 * the i18n cookie). Defaults to zh because that's our primary market.
 */
export function BrandLogo({
  variant,
  locale = 'zh',
  href,
  className = '',
  heightPx,
}: {
  variant: 'horizontal' | 'vertical';
  locale?: 'zh' | 'en';
  /** When set, wraps the logo in a Next.js Link. Useful for top-bar
   *  brand mark that should link back to /. */
  href?: string;
  className?: string;
  /** Override the default height (px). Horizontal default = 32,
   *  vertical default = 96. Aspect ratio is preserved via width:auto. */
  heightPx?: number;
}): JSX.Element {
  const fileLocale = locale === 'en' && variant === 'horizontal' ? 'en' : 'zh';
  const src = `/brand/cea-${variant}-${fileLocale}.png`;
  // Native dimensions from extract-logos.py output:
  //   horizontal-zh: 1630×494  (≈3.30:1)
  //   horizontal-en: 1742×442  (≈3.94:1)
  //   vertical-zh:   1004×1298 (≈0.77:1)
  const nativeWH: Record<string, { w: number; h: number }> = {
    'horizontal-zh': { w: 1630, h: 494 },
    'horizontal-en': { w: 1742, h: 442 },
    'vertical-zh': { w: 1004, h: 1298 },
  };
  const native = nativeWH[`${variant}-${fileLocale}`] ?? nativeWH['horizontal-zh'];
  const h = heightPx ?? (variant === 'horizontal' ? 32 : 96);
  const w = native ? Math.round((native.w / native.h) * h) : h * 3;

  const img = (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt="ChiefEco AI"
      width={w}
      height={h}
      style={{ height: `${h}px`, width: `${w}px` }}
      className={className}
    />
  );

  if (href) {
    return (
      <Link href={href} className="inline-flex items-center" aria-label="ChiefEco AI">
        {img}
      </Link>
    );
  }
  return img;
}
